import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { BookOpen, Inbox, ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/site/Seo";
import { AdminLoginCard } from "@/components/site/admin/AdminLoginCard";
import { adminVerify, adminLogout, adminContactStats, adminListPosts } from "@/lib/api";
import { devLog } from "@/lib/devLog";

const StatCard = ({ label, value, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</p>
    <p className="mt-1 text-2xl font-extrabold text-slate-900">{value ?? "—"}</p>
  </div>
);

export default function AdminLandingPage() {
  const [auth, setAuth] = useState(null);
  const [stats, setStats] = useState({ posts: null, leads: null, leadsNew: null });

  const loadStats = async () => {
    try {
      const [contactStats, posts] = await Promise.all([
        adminContactStats(),
        adminListPosts(),
      ]);
      setStats({ posts: posts.length, leads: contactStats.total, leadsNew: contactStats.new });
    } catch (e) {
      devLog.warn("AdminLanding: stats load failed", e);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await adminVerify();
        setAuth(true);
        await loadStats();
      } catch (e) {
        devLog.warn("AdminLanding: not signed in", e);
        setAuth(false);
      }
    })();
  }, []);

  const handleAuthenticated = async () => {
    setAuth(true);
    await loadStats();
  };

  const handleLogout = async () => {
    try { await adminLogout(); } catch (e) { devLog.warn("AdminLanding: logout failed", e); }
    setAuth(false);
    setStats({ posts: null, leads: null, leadsNew: null });
  };

  if (auth !== true) {
    return <AdminLoginCard onAuthenticated={handleAuthenticated} />;
  }

  return (
    <div data-testid="admin-landing-page">
      <Seo title="Admin Console" path="/admin-clan" noindex />
      <section className="mx-auto w-full max-w-5xl px-4 pb-16 pt-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">Admin</div>
            <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900">Console</h1>
            <p className="mt-1 text-sm text-slate-600">Manage your blog content and review professional inquiries.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={loadStats}
              className="h-10 rounded-lg border-slate-300 bg-white"
              data-testid="admin-landing-refresh"
            >
              <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="h-10 rounded-lg border-slate-300 bg-white"
              data-testid="admin-landing-logout"
            >
              Sign out
            </Button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <StatCard label="Total leads" value={stats.leads} testId="admin-stat-leads-total" />
          <StatCard label="New leads" value={stats.leadsNew} testId="admin-stat-leads-new" />
          <StatCard label="Blog posts" value={stats.posts} testId="admin-stat-posts" />
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2">
          <Link
            to="/admin-clan/blog"
            data-testid="admin-card-blog"
            className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-colors hover:border-blue-300 hover:bg-blue-50/30"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-blue-700">
                <BookOpen className="h-5 w-5" />
              </span>
              <h2 className="text-lg font-extrabold text-slate-900">Manage Blog</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Create, edit, publish, and delete blog posts. Posts appear immediately on the public <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">/blog</code> page.
            </p>
            <span className="mt-5 inline-flex items-center text-sm font-semibold text-blue-700">
              Open blog manager <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>

          <Link
            to="/admin-clan/leads"
            data-testid="admin-card-leads"
            className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-colors hover:border-emerald-300 hover:bg-emerald-50/30"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-50 text-emerald-700">
                <Inbox className="h-5 w-5" />
              </span>
              <h2 className="text-lg font-extrabold text-slate-900">View Leads</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Review every professional inquiry submitted via the contact form. Full payload including budget, timeline, industry verticals, and platform interest.
            </p>
            <span className="mt-5 inline-flex items-center text-sm font-semibold text-emerald-700">
              Open lead inbox <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}
